import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, getApiErrorMessage } from '../utils/api';

/**
 * Single source of truth for admin/engineer/solo auth on the web side.
 *
 * The backend (`/auth/login`, `/auth/signup`, `/auth/verify`, `/auth/me`) is
 * authoritative: it issues a JWT tied to our User/Company tables, and it is
 * the thing that owns subscription + onboarding state. This context wraps
 * those endpoints and keeps the token + cached user in localStorage so the
 * rest of the app can read them synchronously.
 *
 * Notes
 * -----
 * - Supabase is still available for file storage, but it no longer governs
 *   auth: signing in with supabase alone would leave the user without a
 *   company/subscription and is why the old flow was non-functional.
 * - `SOLO` routes to /solo-dashboard. ADMIN/ENGINEER share /dashboard.
 * - Pending onboarding is enforced at the page level by AuthRedirect /
 *   ProtectedRoute rather than hard-coded here, so any authenticated page
 *   may redirect into /onboarding if `user.onboardingStatus === 'PENDING'`.
 */

interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'ENGINEER' | 'SOLO' | 'CLIENT';
  companyId?: string;
  companyName?: string;
  companyLogoUrl?: string;
  onboardingStatus?: 'PENDING' | 'COMPLETED';
  plan?: 'SOLO' | 'COMPANY';
  subscriptionStatus?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setTokenAndUser: (token: string, user: User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';

const mapBackendUser = (raw: any): User => {
  const companyId = raw.companyId || raw.company?.id;
  const plan: 'SOLO' | 'COMPANY' =
    raw.role === 'SOLO' || raw.subscription?.planType?.startsWith('SOLO')
      ? 'SOLO'
      : 'COMPANY';
  return {
    id: raw.id,
    email: raw.email,
    name: raw.name,
    role: raw.role,
    companyId,
    companyName: raw.companyName || raw.company?.name,
    companyLogoUrl: raw.company?.logo,
    onboardingStatus: raw.company?.onboardingStatus || raw.onboardingStatus,
    plan,
    subscriptionStatus: raw.subscription?.status || raw.subscriptionStatus,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setUser(null);
        return;
      }
      const resp = await authApi.getCurrentUser();
      const nextUser = mapBackendUser(resp.data);
      localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      setUser(nextUser);
    } catch (err) {
      // Token stale or revoked — drop and stay signed out.
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    try {
      const resp = await authApi.login(email, password);
      const data = resp.data;
      if (!data?.access_token) throw new Error('Login failed: no access token');
      localStorage.setItem(TOKEN_KEY, data.access_token);
      const nextUser = mapBackendUser(data.user);
      localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      setUser(nextUser);
      routeAfterLogin(nextUser);
    } catch (e) {
      throw new Error(getApiErrorMessage(e));
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // server logout is best-effort; client-side clear is what matters
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
      navigate('/login');
    }
  };

  /**
   * Used by the signup flow after email verification: we already have the
   * JWT and user payload, so no round-trip is needed.
   */
  const setTokenAndUser = (token: string, newUser: User) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setUser(newUser);
  };

  const routeAfterLogin = (u: User) => {
    if (u.onboardingStatus === 'PENDING') {
      navigate('/onboarding');
      return;
    }
    switch (u.role) {
      case 'CLIENT':
        navigate('/client/dashboard');
        return;
      case 'ADMIN':
      case 'ENGINEER':
      case 'SOLO':
      default:
        navigate('/dashboard');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        refreshUser: checkAuth,
        setTokenAndUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export type { User };
